import * as React from "react";
import { RegistrationData } from "../../../components/templates/RegistrationData";
import { IconUserAccountLight, skinVars } from "@telefonica/mistica";
import { observer, useLocalObservable } from "mobx-react-lite";
import { strings } from "../../../localization";
import { useGlobalStore } from "../../../contexts/globalStoreContext";
import { FintechRegistrationReqEnum, Store } from "./store";
import { useAnalytics, useNavigateCustom } from "../../../hooks";
import { isEmailValid } from "../../../utils/validators";
import { cleanString } from "../../../utils/formatters";

const UserRegistrationData: React.FC = observer(() => {
	const { fintechRegisterStore, authenticationStore } = useGlobalStore();
	const navigate = useNavigateCustom();
	const store = useLocalObservable(() => new Store(authenticationStore, fintechRegisterStore));
	const { title, description, calloutDescription } = strings.pages.userRegistrationData;
	const auth = authenticationStore.currentUser;
	const { updatePageView, addAnalyticEvent } = useAnalytics();
	const { buttonPrimary } = strings.components.buttons;
	const { invalidEmail } = strings.organisms.inputsRegistrationData.errors;
	const [isInvalidEmail, setIsInvalidEmail] = React.useState(false);

	const onContinue = async (): Promise<void> => {
		addAnalyticEvent({
			component: "button",
			action: "click.button",
			step: "fgts:cadastro:dados-cadastrais",
			event: "interaction",
			description: buttonPrimary,
		}, { applySanitize: true });

		fintechRegisterStore.name.onChange(cleanString(fintechRegisterStore.name.field.value));
		fintechRegisterStore.setNewEmail(cleanString(fintechRegisterStore.newEmail.value));
		fintechRegisterStore.email.onChange(cleanString(fintechRegisterStore.email.field.value));

		const statusSaveBasicPersonalInfo = await store.saveBasicPersonalInfo();

		if (statusSaveBasicPersonalInfo === FintechRegistrationReqEnum.SUCCESS) {
			const statusSaveBasicPersonalContactPhoneInfo = await store.saveBasicPersonalContactPhoneInfo();

			if (statusSaveBasicPersonalContactPhoneInfo === FintechRegistrationReqEnum.SUCCESS) {
				navigate("/fintech-registration/address");
			}
		}
	};

	const onBack = (): void => {
		navigate(-1);
	};

	const onEmailChange = (value: string): void => {
		const triggerNonInteraction = !isEmailValid(fintechRegisterStore.email.field.value) && !isInvalidEmail;

		if (triggerNonInteraction) {
			setIsInvalidEmail(true);
			addAnalyticEvent({
				component: "body",
				action: "view-error",
				step: "fgts:cadastro:dados-cadastrais",
				event: "non_interaction",
				description: invalidEmail,
			}, { applySanitize: true });
		}

		if (isEmailValid(fintechRegisterStore.email.field.value)) {
			setIsInvalidEmail(false);
		}

		fintechRegisterStore.setNewEmail(value);
		fintechRegisterStore.email.onChange(value);
	};

	const handleNonInteractionEvent = (): void => {
		addAnalyticEvent({
			component: "body",
			action: "view-error",
			step: "fgts:cadastro:dados-cadastrais:alteracao-dados",
			event: "non_interaction",
			description: "alteracao-dados-demais-produtos-financeiros-vivo",
		}, { applySanitize: true });
	};

	React.useEffect(() => {
		updatePageView({
			name: "Vivo Money Cadastro FGTS Dados Iniciais",
			pagePath: "/cadastro-fgts/dados-cadastrais",
		});

		handleNonInteractionEvent();
	}, []);

	return (
		<RegistrationData
			onBack={onBack}
			title={title}
			description={description}
			calloutDescription={calloutDescription}
			icon={<IconUserAccountLight size={64} color={skinVars.colors.brand} />}
			inputsRegistrationDataProps={{
				isDocumentNumberDisabled: true,
				isPhoneNumberDisabled: true,
				isNameDisabled: !!fintechRegisterStore.personal.value,
				documentNumber: auth?.documentNumber || "",
				phoneNumber: auth?.phoneNumber || "",
				name: fintechRegisterStore.name.field.value,
				email: fintechRegisterStore.email.field.value,
				onNameChange: fintechRegisterStore.name.onChange,
				onEmailChange,
			}}
			isLoading={store.loader.isLoading}
			onContinue={onContinue}
		/>
	);
});

export default UserRegistrationData;
